import {
  CustomPromotionSectionDoc,
  ProductGroupForCategorySubset,
  PromotionSection,
  StoreCategory,
} from '@gooduncles/gu-app-schema';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { RootState } from '../store';

export interface HomeState {
  isMenuOpen: boolean;
  selectedStoreCategory: StoreCategory | null;
  productGroupForCategories: {
    [category: string]: ProductGroupForCategorySubset | null;
  };
  promotionSections: (PromotionSection & { _id: string })[];
  customPromotionSections: CustomPromotionSectionDoc[];
}

const initialState: HomeState = {
  isMenuOpen: false,
  selectedStoreCategory: null,
  productGroupForCategories: {},
  promotionSections: [],
  customPromotionSections: [],
};

const homeSlice = createSlice({
  name: 'home',
  initialState,
  reducers: {
    setProductGroupForCategories(
      state,
      action: PayloadAction<{
        [category: string]: ProductGroupForCategorySubset | null;
      }>
    ) {
      state.productGroupForCategories = action.payload;
    },
    setSelectedStoreCategory(state, action) {
      state.selectedStoreCategory = action.payload;
    },
    setIsMenuOpen(state, action) {
      state.isMenuOpen = action.payload;
    },
    setPromotionSections(state, action) {
      state.promotionSections = action.payload;
    },
    setCustomPromotionSections(state, action) {
      state.customPromotionSections = action.payload;
    },
  },
});

export const selectSelectedStoreCategory = (state: RootState) => state.home.selectedStoreCategory;
export const selectStoreCategories = (state: RootState) => {
  const { productGroupForCategories } = state.home;
  const categories = productGroupForCategories
    ? Object.entries(productGroupForCategories)
        .filter(([, v]) => v !== null)
        .map(([key, value]) => {
          return {
            key,
            emoji: value?.emoji ?? '',
            title: value?.title ?? '',
            index: value?.index ?? 6,
          };
        })
    : [];
  return categories;
};
export const selectProductGroupForCategory = (state: RootState, category: string) =>
  state.home.productGroupForCategories?.[category];
export const selectIsMenuOpen = (state: RootState) => state.home.isMenuOpen;
export const selectPromotionSections = (state: RootState) => {
  if (state.home.selectedStoreCategory) {
    return (state.home.promotionSections ?? [])?.filter(
      (promotion) => promotion.storeCategory === null || promotion.storeCategory === state.home.selectedStoreCategory
    );
  }
  return state.home.promotionSections ?? [];
};
export const selectPromotionSection = (state: RootState, id?: string) =>
  id ? state.home.promotionSections.find((v) => v._id === id) : undefined;
export const selectCustomPromotionSections = (state: RootState) => state.home.customPromotionSections ?? [];
export const selectCustomPromotionSection = (state: RootState, id?: string) =>
  id ? state.home.customPromotionSections.find((v) => v._id === id) : undefined;

export const {
  setSelectedStoreCategory,
  setProductGroupForCategories,
  setIsMenuOpen,
  setPromotionSections,
  setCustomPromotionSections,
} = homeSlice.actions;

export default homeSlice.reducer;
